// (imports SAME â€” unchanged)
import { Link } from "react-router-dom";
import { useEffect, useState, useContext } from "react";
import axios from "axios";
import { API_URL } from "../../../config";
import { formatDate } from "../../../utils/dateFormatter";
import { AuthContext } from "../../../Context/AuthContext";
import axiosInstance from "../../../utils/axiosInstance";
import * as XLSX from "xlsx";
import "./Opening.css";

const canDo = (user, module, action) => {
  if (user?.role === "superadmin" || user?.role === "manager") return true;
  return user?.permissions?.[module]?.[action];
};

const canViewPage = (user, module) => {
  if (user?.role === "superadmin" || user?.role === "manager") return true;
  return user?.permissions?.[module]?.view;
};

function Opening() {
  const [Jobs, setJobs] = useState([]);
  const [search, setSearch] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  const [jobTypeFilter, setJobTypeFilter] = useState("");
  const [minSalaryFilter, setMinSalaryFilter] = useState("");
  const [maxSalaryFilter, setMaxSalaryFilter] = useState("");
  const [selectedJob, setSelectedJob] = useState(null);
  const [resumePreviewUrl, setResumePreviewUrl] = useState("");
  const [resumeListJob, setResumeListJob] = useState(null);
  const [uploadResumeJob, setUploadResumeJob] = useState(null);
  const [uploadApplicantName, setUploadApplicantName] = useState("");
  const [uploadResumeFile, setUploadResumeFile] = useState(null);

  const { user } = useContext(AuthContext);
  const itemsPerPage = 5;

  const getNameFromResumeUrl = (url = "") => {
    const clean = String(url).split("?")[0].split("#")[0];
    const file = clean.split("/").pop() || "resume";
    return file.replace(/\.[^/.]+$/, "") || "resume";
  };

  useEffect(() => {
    axios.get(`${API_URL}/api/get_Jobs`).then((res) => {
      if (res.data) setJobs(res.data);
    });
  }, []);

  if (!canViewPage(user, "jobopenings")) {
    return <div style={{ padding: "40px" }}>ðŸš« Access Denied</div>;
  }

  let filteredJobs = Jobs.filter((job) =>
    job.department?.deptName?.toLowerCase().includes(search.toLowerCase()) ||
    job.service?.serviceName?.toLowerCase().includes(search.toLowerCase())
  );

  if (departmentFilter) {
    filteredJobs = filteredJobs.filter(
      (job) => job.department?.deptName === departmentFilter
    );
  }

  if (jobTypeFilter) {
    filteredJobs = filteredJobs.filter(
      (job) => job.job_type === jobTypeFilter
    );
  }

  if (minSalaryFilter) {
    filteredJobs = filteredJobs.filter(
      (job) => job.mini_salary >= Number(minSalaryFilter)
    );
  }

  if (maxSalaryFilter) {
    filteredJobs = filteredJobs.filter(
      (job) => job.max_salary <= Number(maxSalaryFilter)
    );
  }

  const indexOfLast = currentPage * itemsPerPage;
  const currentJobs = filteredJobs.slice(indexOfLast - itemsPerPage, indexOfLast);
  const totalPages = Math.ceil(filteredJobs.length / itemsPerPage);

  const exportToExcel = () => {
    const exportRows = filteredJobs.map((job) => ({
      "Job ID": job.jobId,
      Department: job.department?.deptName || "",
      Designation: job.designation || job.service?.serviceName || "",
      Min: job.mini_salary,
      Max: job.max_salary,
      Type: job.job_type,
      Skills: job.skills,
      Description: job.job_des,
      Posted: job.opend_Date ? formatDate(job.opend_Date) : "",
      "Close Date": job.close_date ? formatDate(job.close_date) : "",
      Openings: job.no_of_Opening,
      Selected: job.selected_emp,
      Available: job.availableVacancies,
    }));
    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Jobs");
    XLSX.writeFile(wb, "Job_Openings.xlsx");
  };

  const handleUpdate = async (job) => {
    const available = Number(job?.availableVacancies ?? 0);
    if (available <= 0) {
      alert("No vacancies available for this job.");
      return;
    }

    try {
      const res = await axios.put(`${API_URL}/api/updateVacancy/${job.jobId}`, {
        count: 1,
      });

      setJobs((prev) =>
        prev.map((row) =>
          row.jobId === job.jobId
            ? {
                ...row,
                selected_emp: res.data.job.selected_emp,
                availableVacancies: res.data.job.availableVacancies,
              }
            : row
        )
      );
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        "Unable to select candidate for this vacancy.";
      alert(msg);
    }
  };

  const handleSelectResume = async (job, resumeUrl) => {
    const available = Number(job?.availableVacancies ?? 0);
    if (available <= 0) {
      alert("No vacancies available for this job.");
      return;
    }

    try {
      const res = await axiosInstance.put(`${API_URL}/api/selectResumeForJob/${job.jobId}`, {
        resumeUrl,
      });

      const updatedJob = res?.data?.job;
      if (updatedJob) {
        setJobs((prev) =>
          prev.map((row) => (row.jobId === updatedJob.jobId ? updatedJob : row))
        );
        return updatedJob;
      }
      alert("Resume selected successfully.");
      return null;
    } catch (error) {
      alert(error?.response?.data?.message || "Unable to select resume.");
      return null;
    }
  };

  const handleUnselectResume = async (job, resumeUrl) => {
    const ok = window.confirm("Are you sure you want to unselect this resume?");
    if (!ok) return null;

    try {
      const res = await axiosInstance.put(`${API_URL}/api/unselectResumeForJob/${job.jobId}`, {
        resumeUrl,
      });

      const updatedJob = res?.data?.job;
      if (updatedJob) {
        setJobs((prev) =>
          prev.map((row) => (row.jobId === updatedJob.jobId ? updatedJob : row))
        );
        return updatedJob;
      }
      return null;
    } catch (error) {
      alert(error?.response?.data?.message || "Unable to unselect resume.");
      return null;
    }
  };

  const handleBlockResume = async (job, resumeUrl) => {
    const ok = window.confirm("Block this resume for this job?");
    if (!ok) return null;

    try {
      const res = await axiosInstance.put(`${API_URL}/api/blockResumeForJob/${job.jobId}`, {
        resumeUrl,
      });

      const updatedJob = res?.data?.job;
      if (updatedJob) {
        setJobs((prev) =>
          prev.map((row) => (row.jobId === updatedJob.jobId ? updatedJob : row))
        );
        return updatedJob;
      }
      return null;
    } catch (error) {
      alert(error?.response?.data?.message || "Unable to block resume.");
      return null;
    }
  };

  const handleUnblockResume = async (job, resumeUrl) => {
    const ok = window.confirm("Unblock this resume for this job?");
    if (!ok) return null;

    try {
      const res = await axiosInstance.put(`${API_URL}/api/unblockResumeForJob/${job.jobId}`, {
        resumeUrl,
      });

      const updatedJob = res?.data?.job;
      if (updatedJob) {
        setJobs((prev) =>
          prev.map((row) => (row.jobId === updatedJob.jobId ? updatedJob : row))
        );
        return updatedJob;
      }
      return null;
    } catch (error) {
      alert(error?.response?.data?.message || "Unable to unblock resume.");
      return null;
    }
  };

  const deleteJob = async (jobId) => {
    if (window.confirm("Delete job?")) {
      await axiosInstance.delete(`${API_URL}/api/deleteJob/${jobId}`);
      setJobs((prev) => prev.filter((job) => job._id !== jobId));
    }
  };

  const handleResumeUpload = async (job, file, applicantName) => {
    if (!file) return;

    const isPdfMime = file.type === "application/pdf" || file.type === "application/x-pdf" || file.type === "";
    const isPdfName = file.name?.toLowerCase().endsWith(".pdf");
    if (!isPdfName && !isPdfMime) {
      alert("Only PDF resume files are allowed.");
      return;
    }

    try {
      const data = new FormData();
      data.append("resumeFile", file);
      data.append("applicantName", applicantName || "");
      const res = await axiosInstance.put(`${API_URL}/api/uploadJobResume/${job._id}`, data);

      const updatedJob = res?.data?.data;
      if (updatedJob) {
        setJobs((prev) =>
          prev.map((row) => (row._id === updatedJob._id ? updatedJob : row))
        );
        if (resumeListJob && resumeListJob._id === updatedJob._id) {
          setResumeListJob(updatedJob);
        }
      }
      alert("Resume uploaded successfully.");
    } catch (error) {
      alert(error?.response?.data?.message || "Failed to upload resume.");
    }
  };

  const getResumeList = (job) => {
    if (Array.isArray(job?.resumeApplicants) && job.resumeApplicants.length > 0) {
      return job.resumeApplicants
        .filter((item) => item?.resumeUrl)
        .map((item) => ({
          url: item.resumeUrl,
          applicantName: (item.applicantName || "").trim() || getNameFromResumeUrl(item.resumeUrl),
        }));
    }
    if (Array.isArray(job?.resumeFiles) && job.resumeFiles.length > 0) {
      return job.resumeFiles.filter(Boolean).map((url) => ({
        url,
        applicantName: getNameFromResumeUrl(url),
      }));
    }
    if (job?.resumeFile) {
      return [{ url: job.resumeFile, applicantName: getNameFromResumeUrl(job.resumeFile) }];
    }
    return [];
  };

  const totalOpenings = Jobs.reduce((a, j) => a + j.no_of_Opening, 0);
  const totalSelected = Jobs.reduce((a, j) => a + j.selected_emp, 0);
  const totalAvailable = Jobs.reduce((a, j) => a + j.availableVacancies, 0);

  const departments = [...new Set(Jobs.map(j => j.department?.deptName).filter(Boolean))];
  const kpiCards = [
    {
      label: "Total Jobs",
      value: Jobs.length,
      accent: "#7c8cff",
      gradient: "linear-gradient(135deg,#0b1324,#182945,#22365e)",
      glow: "0 0 0 1px rgba(124,140,255,0.35), 0 0 20px rgba(124,140,255,0.28), inset 0 0 30px rgba(124,140,255,0.09)",
    },
    {
      label: "Openings",
      value: totalOpenings,
      accent: "#46e0b1",
      gradient: "linear-gradient(135deg,#071a1b,#123238,#18484f)",
      glow: "0 0 0 1px rgba(70,224,177,0.35), 0 0 20px rgba(70,224,177,0.25), inset 0 0 30px rgba(70,224,177,0.08)",
    },
    {
      label: "Selected",
      value: totalSelected,
      accent: "#f9b14d",
      gradient: "linear-gradient(135deg,#1b1520,#332539,#4b2f46)",
      glow: "0 0 0 1px rgba(249,177,77,0.35), 0 0 20px rgba(249,177,77,0.25), inset 0 0 30px rgba(249,177,77,0.08)",
    },
    {
      label: "Available",
      value: totalAvailable,
      accent: "#ff6e8a",
      gradient: "linear-gradient(135deg,#1b1423,#322440,#4b3157)",
      glow: "0 0 0 1px rgba(255,110,138,0.35), 0 0 20px rgba(255,110,138,0.24), inset 0 0 30px rgba(255,110,138,0.08)",
    }
  ];

  return (
    <div className="opening-page">
      <div className="opening-shell">

      {/* HEADER SAME */}

      {/* TOP BAR */}
      <div className="opening-toolbar">
        <div className="opening-toolbar-left">

          {/* ðŸ” SEARCH */}
          <input
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="opening-control opening-search"
          />

          {/* ðŸ¢ DEPARTMENT */}
          <select
            onChange={(e) => setDepartmentFilter(e.target.value)}
            className="opening-control"
          >
            <option value="">All Departments</option>
            {departments.map((d, i) => <option key={i}>{d}</option>)}
          </select>

          {/* ðŸ§‘â€ðŸ’¼ TYPE */}
          <select
            value={jobTypeFilter}
            onChange={(e) => setJobTypeFilter(e.target.value)}
            className="opening-control"
          >
            <option value="">All Types</option>
            <option value="Full Time">Full Time</option>
            <option value="Part Time">Part Time</option>
          </select>

          {/* ðŸ’° SALARY */}
          <input
            type="number"
            placeholder="Min Salary"
            value={minSalaryFilter}
            onChange={(e) => setMinSalaryFilter(e.target.value)}
            className="opening-control opening-salary"
          />

          <input
            type="number"
            placeholder="Max Salary"
            value={maxSalaryFilter}
            onChange={(e) => setMaxSalaryFilter(e.target.value)}
            className="opening-control opening-salary"
          />

          {/* BUTTONS SAME */}
          <button className="opening-btn opening-btn-export" onClick={exportToExcel}>
            Export
          </button>

          <button className="opening-btn opening-btn-reset" onClick={()=>{
            setSearch("");setDepartmentFilter("");setJobTypeFilter("");setMinSalaryFilter("");setMaxSalaryFilter("");
          }}>
            Reset
          </button>

        </div>

        {/* ADD VACANCY BUTTON SAME */}

<Link
  to="/admin/addJobs"
  className="opening-add-btn"
>
  Add Vacancy
</Link>
      </div>

      

     

      {/* CARDS */}
      <div className="opening-kpi-grid">
        {kpiCards.map((item, i) => (
          <div
            key={i}
            className="opening-kpi-card"
            style={{ "--opening-accent": item.accent, borderColor: `${item.accent}55` }}
          >
            <p className="opening-kpi-label">
              {item.label}
            </p>
            <h2 className="opening-kpi-value">
              {item.value}
            </h2>
          </div>
        ))}
      </div>

      {/* TABLE */}
      <div className="opening-table-wrap">
        <table className="opening-table">
          <thead>
            <tr>
              {["Job ID","Department","Designation","Min","Max","Type","Skills","Description","Posted","Close Date","Openings","Selected","Available","Action"]
                .map((h,i)=>(
                  <th key={i}>{h}</th>
              ))}
            </tr>
          </thead>

          <tbody>
            {currentJobs.map((job) => (
              <tr key={job.jobId}>
                <td>{job.jobId}</td>
                <td>{job.department?.deptName}</td>
                <td className="opening-designation">{job.designation || job.service?.serviceName}</td>
                <td>₹{job.mini_salary}</td>
                <td>₹{job.max_salary}</td>
                <td>{job.job_type}</td>
                <td className="opening-skills">{job.skills}</td>
                <td>{job.job_des}</td>
                <td>{formatDate(job.opend_Date)}</td>
                <td>{formatDate(job.close_date)}</td>
                <td>{job.no_of_Opening}</td>
                <td>{job.selected_emp}</td>
                <td className={job.availableVacancies <= 0 ? "opening-danger" : "opening-success"}>
                  {job.availableVacancies}
                </td>

                <td className="opening-action-col">
                  <button
                    onClick={() => setSelectedJob(job)}
                    className="opening-btn opening-btn-view"
                  >
                    View
                  </button>
                  <button
                    onClick={() => {
                      setUploadResumeJob(job);
                      setUploadApplicantName("");
                      setUploadResumeFile(null);
                    }}
                    className="opening-btn opening-btn-upload"
                  >
                    Upload Resume
                  </button>
                  {getResumeList(job).length > 0 ? (
                    <button
                      onClick={() => setResumeListJob(job)}
                      className="opening-btn opening-btn-resume"
                    >
                      View Resume
                    </button>
                  ) : null}

                  <button
                    onClick={() => deleteJob(job._id)}
                    className="opening-btn opening-btn-delete"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* PAGINATION */}
      {totalPages > 1 && (
        <div className="opening-pagination">
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              onClick={() => setCurrentPage(i + 1)}
              className={`opening-page-btn ${currentPage === i + 1 ? "active" : ""}`}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}

      {selectedJob && (
        <div
          onClick={() => setSelectedJob(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.65)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "16px"
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(760px, 100%)",
              maxHeight: "90vh",
              overflowY: "auto",
              borderRadius: "14px",
              background: "linear-gradient(145deg,#0b1220,#1f2937)",
              border: "1px solid #334155",
              boxShadow: "0 18px 35px rgba(0,0,0,0.45)",
              color: "#e5e7eb",
              padding: "18px"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
              <h3 style={{ margin: 0, color: "#c7d2fe" }}>Vacancy Details</h3>
              <button
                onClick={() => setSelectedJob(null)}
                style={{
                  background: "#ef4444",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  padding: "6px 10px",
                  cursor: "pointer"
                }}
              >
                Close
              </button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: "10px" }}>
              <div><strong style={{ color: "#93c5fd" }}>Job ID:</strong> {selectedJob.jobId || "-"}</div>
              <div><strong style={{ color: "#93c5fd" }}>Department:</strong> {selectedJob.department?.deptName || "-"}</div>
              <div><strong style={{ color: "#93c5fd" }}>Designation:</strong> {selectedJob.designation || selectedJob.service?.serviceName || "-"}</div>
              <div><strong style={{ color: "#93c5fd" }}>Job Type:</strong> {selectedJob.job_type || "-"}</div>
              <div><strong style={{ color: "#93c5fd" }}>Min Salary:</strong> ₹{selectedJob.mini_salary ?? 0}</div>
              <div><strong style={{ color: "#93c5fd" }}>Max Salary:</strong> ₹{selectedJob.max_salary ?? 0}</div>
              <div><strong style={{ color: "#93c5fd" }}>Openings:</strong> {selectedJob.no_of_Opening ?? 0}</div>
              <div><strong style={{ color: "#93c5fd" }}>Selected:</strong> {selectedJob.selected_emp ?? 0}</div>
              <div><strong style={{ color: "#93c5fd" }}>Available:</strong> {selectedJob.availableVacancies ?? 0}</div>
              <div><strong style={{ color: "#93c5fd" }}>Posted Date:</strong> {selectedJob.opend_Date ? formatDate(selectedJob.opend_Date) : "-"}</div>
              <div><strong style={{ color: "#93c5fd" }}>Close Date:</strong> {selectedJob.close_date ? formatDate(selectedJob.close_date) : "-"}</div>
            </div>

            <div style={{ marginTop: "14px" }}>
              <strong style={{ color: "#93c5fd" }}>Skills:</strong>
              <p style={{ margin: "6px 0 0", color: "#38bdf8", wordBreak: "break-word" }}>
                {selectedJob.skills || "-"}
              </p>
            </div>

            <div style={{ marginTop: "14px" }}>
              <strong style={{ color: "#93c5fd" }}>Description:</strong>
              <p style={{ margin: "6px 0 0", lineHeight: 1.6, color: "#dbeafe", whiteSpace: "pre-wrap" }}>
                {selectedJob.job_des || "-"}
              </p>
            </div>
          </div>
        </div>
      )}

      {resumePreviewUrl && (
        <div
          onClick={() => setResumePreviewUrl("")}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.65)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1001,
            padding: "16px"
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(900px, 100%)",
              height: "min(90vh, 100%)",
              borderRadius: "12px",
              background: "#ffffff",
              border: "1px solid #cbd5e1",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", borderBottom: "1px solid #e2e8f0" }}>
              <strong>Resume</strong>
              <div style={{ display: "flex", gap: "8px" }}>
                <a
                  href={resumePreviewUrl}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="opening-btn opening-btn-download"
                >
                  Download Resume
                </a>
                <button onClick={() => setResumePreviewUrl("")} className="opening-btn opening-btn-delete">Close</button>
              </div>
            </div>
            <iframe
              title="Resume Preview"
              src={resumePreviewUrl}
              style={{ border: "none", width: "100%", height: "100%" }}
            />
          </div>
        </div>
      )}

      {uploadResumeJob && (
        <div
          onClick={() => setUploadResumeJob(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(7, 20, 37, 0.62)",
            backdropFilter: "blur(3px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1003,
            padding: "16px"
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(560px, 100%)",
              borderRadius: "16px",
              background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
              border: "1px solid #d6e2ef",
              boxShadow: "0 20px 50px rgba(15, 23, 42, 0.28)",
              padding: "16px"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", borderBottom: "1px solid #dbe7f3", paddingBottom: "10px" }}>
              <strong style={{ fontSize: "26px", color: "#123b63", letterSpacing: "0.2px" }}>
                Upload Resume
              </strong>
              <button onClick={() => setUploadResumeJob(null)} className="opening-btn opening-btn-delete">Close</button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", alignItems: "end" }}>
              <div>
                <label style={{ display: "block", marginBottom: "6px", color: "#1e3550", fontWeight: 600 }}>Applicant Name</label>
                <input
                  type="text"
                  value={uploadApplicantName}
                  onChange={(e) => setUploadApplicantName(e.target.value)}
                  placeholder="Enter applicant name"
                  className="opening-control"
                  style={{ width: "100%" }}
                />
              </div>
              <div>
                <label style={{ display: "block", marginBottom: "6px", color: "#1e3550", fontWeight: 600 }}>Resume File (PDF)</label>
                <input
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={(e) => setUploadResumeFile(e.target.files?.[0] || null)}
                  className="opening-control"
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "14px" }}>
              <button
                onClick={() => setUploadResumeJob(null)}
                className="opening-btn opening-btn-delete"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!uploadApplicantName.trim()) {
                    alert("Applicant name is required.");
                    return;
                  }
                  if (!uploadResumeFile) {
                    alert("Please choose a resume file.");
                    return;
                  }
                  await handleResumeUpload(uploadResumeJob, uploadResumeFile, uploadApplicantName.trim());
                  setUploadResumeJob(null);
                  setUploadApplicantName("");
                  setUploadResumeFile(null);
                }}
                className="opening-btn opening-btn-upload"
              >
                Upload
              </button>
            </div>
          </div>
        </div>
      )}

      {resumeListJob && (
        <div
          onClick={() => setResumeListJob(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(7, 20, 37, 0.62)",
            backdropFilter: "blur(3px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1002,
            padding: "16px"
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(620px, 100%)",
              maxHeight: "80vh",
              overflowY: "auto",
              borderRadius: "16px",
              background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
              border: "1px solid #d6e2ef",
              boxShadow: "0 20px 50px rgba(15, 23, 42, 0.28)",
              padding: "16px"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", borderBottom: "1px solid #dbe7f3", paddingBottom: "10px" }}>
              <strong style={{ fontSize: "31px", color: "#123b63", letterSpacing: "0.2px" }}>
                Uploaded Resumes ({getResumeList(resumeListJob).length})
              </strong>
              <button onClick={() => setResumeListJob(null)} className="opening-btn opening-btn-delete">Close</button>
            </div>

            <div style={{ display: "grid", gap: "10px" }}>
              {getResumeList(resumeListJob).map((item, index) => (
                (() => {
                  const isSelected =
                    Array.isArray(resumeListJob?.selectedResumes) &&
                    resumeListJob.selectedResumes.includes(item.url);
                  const isBlocked =
                    Array.isArray(resumeListJob?.blockedResumes) &&
                    resumeListJob.blockedResumes.includes(item.url);

                  return (
                <div
                  key={`${item.url}-${index}`}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    border: "1px solid #d4e0ec",
                    borderRadius: "11px",
                    background: "linear-gradient(180deg, #ffffff, #f3f8ff)",
                    boxShadow: "0 4px 12px rgba(15, 23, 42, 0.08)",
                    padding: "9px 12px"
                  }}
                >
                  <span style={{ fontWeight: 600, color: "#1e3550" }}>{item.applicantName}.resume</span>
                  <div style={{ display: "flex", gap: "8px" }}>
                    {!isBlocked && (
                      <button
                        className="opening-btn opening-btn-view"
                        onClick={() => {
                          setResumePreviewUrl(item.url);
                          setResumeListJob(null);
                        }}
                      >
                        View
                      </button>
                    )}
                    <button
                      className="opening-btn opening-btn-select"
                      onClick={async () => {
                        if (isBlocked) {
                          alert("This resume is blocked. Unblock it first to select.");
                          return;
                        }
                        const updated = isSelected
                          ? await handleUnselectResume(resumeListJob, item.url)
                          : await handleSelectResume(resumeListJob, item.url);
                        if (updated) setResumeListJob(updated);
                      }}
                      disabled={
                        isBlocked ||
                        Number(resumeListJob?.availableVacancies ?? 0) <= 0 &&
                        !isSelected
                      }
                      title={
                        isBlocked
                          ? "Resume is blocked"
                          :
                        isSelected
                          ? "Unselect candidate"
                          : Number(resumeListJob?.availableVacancies ?? 0) <= 0
                          ? "No vacancies available"
                          : "Select candidate"
                      }
                    >
                      {isSelected ? "Unselect" : "Select"}
                    </button>
                    <button
                      className="opening-btn"
                      onClick={async () => {
                        const updated = isBlocked
                          ? await handleUnblockResume(resumeListJob, item.url)
                          : await handleBlockResume(resumeListJob, item.url);
                        if (updated) setResumeListJob(updated);
                      }}
                      style={{
                        background: isBlocked ? "#059669" : "#b91c1c",
                        color: "#fff"
                      }}
                      title={
                        isBlocked
                          ? "Unblock resume"
                          : "Block resume"
                      }
                    >
                      {isBlocked ? "Unblock" : "Block"}
                    </button>
                  </div>
                </div>
                  );
                })()
              ))}
            </div>
          </div>
        </div>
      )}

      </div>
    </div>
  );
}

export default Opening;


